/**
 * /settings — Surface Layer (/01)
 *
 * Interface/theme (real: GET/PATCH /api/user-settings + live preview via
 * the theme provider), Profile (GET /api/me), and Integrations
 * (GET /api/surface-contracts?kind=plugins). Carbon Tabs/Dropdown.
 */

import { useState } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { useQueryClient } from '@tanstack/react-query';
import {
  Button,
  Column,
  Dropdown,
  Grid,
  InlineNotification,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Tag,
  Tile,
} from '@carbon/react';
import { useSessionThemeStore, useSurfaceTheme } from '@theme/CarbonSurfaceThemeProvider';
import { useUserInterfaceSettings } from '@lib/persistence/useUserInterfaceSettings';
import { resolvedString, isForced, forcedByLabel } from '@contracts/user-settings.contract';
import { usePluginContracts } from '@lib/persistence/useSurfaceContracts';
import { useMe } from '@lib/persistence/useRuntimeData';
import { useEntitlements } from '@lib/persistence/useEntitlements';
import { DevHubLayoutSettings } from '../components/settings/DevHubLayoutSettings';
import { bffPatch, SURFACE_BFF_PATHS } from '@lib/idl-bridge/bff-read-client';
import type { CarbonThemeName, SessionThemeEdit, SurfaceDensity } from '@contracts/theme.contract';
import '../styles/views.scss';

export const Route = createFileRoute('/settings')({
  component: SettingsView,
});

const THEME_ITEMS: CarbonThemeName[] = ['g100', 'g90', 'g10', 'white'];
const DENSITY_ITEMS: SurfaceDensity[] = ['compact', 'comfortable', 'spacious'];

function buildEdit(baseTheme: CarbonThemeName, density: SurfaceDensity): SessionThemeEdit {
  return { baseTheme, surfaceTokens: { 'surface-density': density } };
}

function SettingsView() {
  const queryClient = useQueryClient();
  const { merged, setUserPreference } = useSurfaceTheme();
  const setSessionEdit = useSessionThemeStore((s) => s.setEdit);
  const { resolution, usingFallback } = useUserInterfaceSettings('view', 'settings.interface');
  const { me, memberships } = useMe();
  const { isElevated } = useEntitlements();
  const plugins = usePluginContracts();

  const [baseTheme, setBaseTheme] = useState<CarbonThemeName>(merged.baseTheme);
  const [density, setDensity] = useState<SurfaceDensity>(
    (merged.surfaceTokens['surface-density'] as SurfaceDensity | undefined) ?? 'comfortable'
  );
  const [saved, setSaved] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  // Matrioska cascade: a key forced by an ancestor (depth-0 master or any ancestor)
  // is locked here — the control is disabled and the write-guard would reject it anyway.
  const themeForced = isForced(resolution, 'baseTheme');
  const densityForced = isForced(resolution, 'density');
  const forcedEntries = Object.entries(resolution.forcedBy ?? {});

  function preview(nextTheme: CarbonThemeName, nextDensity: SurfaceDensity) {
    setSessionEdit(buildEdit(nextTheme, nextDensity));
    setSaved(false);
  }

  function savePreferences() {
    setUserPreference({
      schemaVersion: 1,
      identityId: me?.id ?? resolution.userId ?? 'session',
      baseTheme,
      tokens: {},
      surfaceTokens: { 'surface-density': density },
      updatedAt: new Date().toISOString(),
    });
    setSessionEdit(null);
    // ROOT FIX (queue hygiene): no longer enqueue a `user_preferences.update_theme` ActionFrame —
    // it had NO worker consumer, so every theme save left a frame stuck `queued` forever. The
    // preference is durably persisted by the PATCH /api/user-settings below; the frame was redundant.
    setSaving(true);
    setSaveError(null);
    void bffPatch<{ ok: boolean }>(SURFACE_BFF_PATHS.userSettings, {
      scope_type: 'view',
      scope_key: 'settings.interface',
      settings: { baseTheme, density },
      layout: { density },
    }).then((res) => {
      setSaving(false);
      if (res.ok) {
        void queryClient.invalidateQueries({ queryKey: ['user-settings'] });
        setSaved(true);
      } else {
        setSaveError(res.error.message);
      }
    });
  }

  return (
    <div className="view">
      <header className="view__header">
        <h1 className="view__title">Settings</h1>
        <p className="view__subtitle">Interface, profile and integrations for your workspace.</p>
      </header>

      <Tabs>
        <TabList aria-label="Settings sections">
          <Tab>Interface</Tab>
          <Tab>Profile</Tab>
          <Tab>Integrations</Tab>
          {isElevated ? <Tab>Layout &amp; design</Tab> : null}
        </TabList>
        <TabPanels>
          <TabPanel>
            <div className="surface-grid">
              <Grid condensed>
                <Column sm={4} md={4} lg={8}>
                  <Tile className="view-tile">
                    <h2 className="view-tile__value">Interface &amp; theme</h2>
                    <Dropdown
                      id="settings-theme"
                      titleText="Carbon theme"
                      label="Theme"
                      disabled={themeForced}
                      helperText={themeForced ? (forcedByLabel(resolution, 'baseTheme') ?? undefined) : undefined}
                      items={THEME_ITEMS}
                      selectedItem={baseTheme}
                      itemToString={(item: CarbonThemeName | null) => item ?? ''}
                      onChange={({ selectedItem }: { selectedItem: CarbonThemeName | null }) => {
                        if (!selectedItem) return;
                        setBaseTheme(selectedItem);
                        preview(selectedItem, density);
                      }}
                    />
                    <Dropdown
                      id="settings-density"
                      titleText="Density"
                      label="Density"
                      disabled={densityForced}
                      helperText={densityForced ? (forcedByLabel(resolution, 'density') ?? undefined) : undefined}
                      items={DENSITY_ITEMS}
                      selectedItem={density}
                      itemToString={(item: SurfaceDensity | null) => item ?? ''}
                      onChange={({ selectedItem }: { selectedItem: SurfaceDensity | null }) => {
                        if (!selectedItem) return;
                        setDensity(selectedItem);
                        preview(baseTheme, selectedItem);
                      }}
                    />
                    <div className="view-actions">
                      <Button disabled={saving} onClick={savePreferences}>Save preferences</Button>
                    </div>
                    {saved ? (
                      <InlineNotification
                        kind="success"
                        lowContrast
                        hideCloseButton
                        title="Saved"
                        subtitle="Preferences persisted via /api/user-settings."
                      />
                    ) : null}
                    {saveError ? (
                      <InlineNotification
                        kind="error"
                        lowContrast
                        hideCloseButton
                        title="Save failed"
                        subtitle={
                          saveError.startsWith('forced_setting_key')
                            ? `That setting is locked by your workspace policy (${saveError.split(':')[1] ?? ''}) and can't be changed here.`
                            : saveError
                        }
                      />
                    ) : null}
                  </Tile>
                </Column>
                <Column sm={4} md={4} lg={8}>
                  <Tile className="view-tile">
                    <h2 className="view-tile__value">Resolution</h2>
                    <div className="view-tile__row">
                      <span>Source</span>
                      <Tag type={usingFallback ? 'cool-gray' : 'green'}>
                        {usingFallback ? 'fallback (signed-out / no backend)' : 'live /api/user-settings'}
                      </Tag>
                    </div>
                    <p className="view-tile__label">
                      Resolved base theme: {resolvedString(resolution, 'settings', 'baseTheme', 'g100')}
                    </p>
                    {forcedEntries.length > 0 ? (
                      <>
                        <p className="view-tile__label">Locked by workspace policy (Matrioska cascade):</p>
                        {forcedEntries.map(([key, origin]) => (
                          <div key={key} className="view-tile__row">
                            <span>{key}</span>
                            <Tag type="gray">{`depth ${origin.depth}${origin.depth === 0 ? ' (master)' : ''}`}</Tag>
                          </div>
                        ))}
                      </>
                    ) : (
                      <p className="view-tile__label">Forced keys: {resolution.forcedKeys.length}</p>
                    )}
                    {resolution.chain && resolution.chain.length > 1 ? (
                      <p className="view-tile__label">
                        Settings cascade depth: {resolution.chain.length} tenant level(s), root-first.
                      </p>
                    ) : null}
                  </Tile>
                </Column>
              </Grid>
            </div>
          </TabPanel>

          <TabPanel>
            <div className="surface-grid">
              <Grid condensed>
                <Column sm={4} md={8} lg={16}>
                  <Tile className="view-tile">
                    <h2 className="view-tile__value">Profile</h2>
                    <p className="view-tile__label">Email: {me?.email ?? '—'}</p>
                    <p className="view-tile__label">Display: {me?.displayLabel ?? '—'}</p>
                    <p className="view-tile__label">Role: {me?.role ?? '—'}</p>
                    <p className="view-tile__label">Active tenant: {me?.activeTenantId ?? '—'}</p>
                    <div className="view-tile__row">
                      <span>Workspace</span>
                      <Tag type={me?.provisioned ? 'green' : 'cool-gray'}>
                        {me?.provisioned ? 'provisioned' : 'pending'}
                      </Tag>
                    </div>
                    <p className="view-tile__label">Memberships: {memberships.length}</p>
                  </Tile>
                </Column>
              </Grid>
            </div>
          </TabPanel>

          <TabPanel>
            <div className="surface-grid">
              <Grid condensed>
                <Column sm={4} md={8} lg={16}>
                  <div className="view-stack">
                    <div className="view-tile__row">
                      <h2 className="view-tile__value">Integrations</h2>
                      <Tag type={plugins.fromBackend ? 'green' : 'cool-gray'}>
                        {plugins.fromBackend ? 'live surface-contracts' : 'none'}
                      </Tag>
                    </div>
                    {plugins.rows && plugins.rows.length > 0 ? (
                      plugins.rows.map((p) => (
                        <div key={`${p.plugin_key}:${p.spoke_key}`} className="notification-row">
                          <strong>{p.plugin_key}</strong>
                          <div className="notification-row__meta">
                            <Tag type="cool-gray">{p.status}</Tag>
                            <span>{p.spoke_key}</span>
                          </div>
                        </div>
                      ))
                    ) : (
                      <Tile className="view-empty">
                        <h3>No plugin catalog entries</h3>
                        <p className="view-tile__label">
                          Sign in to a provisioned workspace to see installable plugins.
                        </p>
                      </Tile>
                    )}
                  </div>
                </Column>
              </Grid>
            </div>
          </TabPanel>

          {isElevated ? (
            <TabPanel>
              <div className="surface-grid">
                <Grid condensed>
                  <Column sm={4} md={8} lg={16}>
                    <DevHubLayoutSettings roleIsOperator={isElevated} />
                  </Column>
                </Grid>
              </div>
            </TabPanel>
          ) : null}
        </TabPanels>
      </Tabs>
    </div>
  );
}
